/* Servicio del módulo de Eventos.
 * Centraliza las llamadas al backend (prefijo /eventos/...).
 * El token de autenticación lo agrega el interceptor de ./api.
 */
import api from "./api";

// ----------------------------- Tipos -----------------------------

export interface EventoResumen {
  id: number;
  tema: string;
  lugar: string | null;
  estado: string;
  requiere_evaluacion: boolean | number;
  fecha_inicio_invitacion: string;
  fecha_fin_confirmacion: string;
  fecha_inicio_evento: string;
  fecha_fin_evento: string;
  fecha_creacion?: string;
  total_invitados?: number;
  total_confirmados?: number;
  total_presentes?: number;
}

export interface PreguntaOpcion {
  id?: number;
  orden?: number;
  texto: string;
}

export interface Pregunta {
  id?: number;
  orden?: number;
  texto: string;
  tipo: "OPCION_MULTIPLE" | "ABIERTA";
  obligatoria: boolean | number;
  opciones?: PreguntaOpcion[];
}

export interface Segmento {
  id?: number;
  nombre: string;
  orden?: number;
}

export interface EventoItem {
  id?: number;
  orden?: number;
  nombre: string;
  descripcion?: string | null;
  precio: number;
  cupo?: number | null;
  /** Unidades ya pedidas por todos los invitados (informativo, viene del backend). */
  pedidos_totales?: number;
  /** Unidades que este invitado todavía puede pedir (null = sin límite). */
  disponibles?: number | null;
}

export interface PedidoLinea {
  item_id: number;
  nombre?: string;
  cantidad: number;
  /** Cuánto retiró efectivamente (null hasta que se registra el retiro). */
  cantidad_retirada?: number | null;
  precio_unitario?: number;
  subtotal?: number;
}

export interface RetiroInfo {
  id: number;
  fecha_retiro: string;
  registrado_por: number;
  metodo: "CEDULA" | "QR";
  total_retirado: number;
}

export interface EventoDetalle extends EventoResumen {
  descripcion?: string | null;
  invitados_por_defecto: number;
  recordatorio_horas_antes: number | null;
  requiere_segmentacion?: boolean | number;
  /** false/0 = el evento no maneja acompañantes (ej. venta de platos). */
  permite_acompanantes?: boolean | number;
  /** false/0 = habilitado para TODOS los funcionarios activos (sin lista de invitados). */
  requiere_invitacion?: boolean | number;
  /** true/1 = los invitados deben responder sí/no (bloquea recibos/horas/reclamos). */
  respuesta_obligatoria?: boolean | number;
  /** true/1 = el evento solo es visible para el padrón administrativo del DWH
   *  (mensualeros ADM vigentes salvo MANTENIMIENTO + cargos de fiscalización). */
  solo_administrativos?: boolean | number;
  requiere_pedido?: boolean | number;
  requiere_retiro?: boolean | number;
  requiere_firma?: boolean | number;
  requiere_qr?: boolean | number;
  /** El invitado puede elegir en cuántas cuotas se descuenta lo retirado. */
  permite_cuotas?: boolean | number;
  max_cuotas?: number | null;
  preguntas?: Pregunta[];
  segmentos?: Segmento[];
  items?: EventoItem[];
  tomadores?: Array<{ id: number; cedula: number; nombre: string; activo: boolean | number }>;
}

export interface UsuarioActivo {
  cedula: string;
  nombre: string;
  cargo: string;
}

export interface InvitadoInput {
  cedula: number | string;
  nombre?: string;
  cargo?: string;
  es_reconocido?: boolean;
  max_acompanantes?: number;
  segmento_id?: number | null;
}

export interface Invitacion {
  /** null en eventos habilitados para todos cuando la persona aún no confirmó/pidió. */
  invitado_id: number | null;
  evento_id: number;
  tema: string;
  descripcion?: string | null;
  lugar: string | null;
  fecha_inicio_evento: string;
  fecha_fin_evento: string;
  fecha_fin_confirmacion: string;
  evento_estado: string;
  requiere_evaluacion: boolean | number;
  requiere_invitacion: boolean | number;
  respuesta_obligatoria: boolean | number;
  solo_administrativos?: boolean | number;
  requiere_pedido: boolean | number;
  requiere_retiro: boolean | number;
  requiere_firma: boolean | number;
  requiere_qr: boolean | number;
  es_reconocido: boolean | number;
  max_acompanantes: number;
  estado_confirmacion: "PENDIENTE" | "CONFIRMADO" | "RECHAZADO";
  acompanantes_confirmados: number | null;
  presente: boolean | number;
  evaluacion_completada: boolean | number;
}

const Eventos = {
  // -------------------- Admin --------------------
  async esAdmin(): Promise<boolean> {
    try {
      const { data } = await api.get(`/eventos/admin/es-admin`);
      return Boolean(data?.es_admin);
    } catch {
      return false;
    }
  },

  async listarEventos(): Promise<EventoResumen[]> {
    const { data } = await api.get(`/eventos/admin/eventos`);
    return (data?.datos || []) as EventoResumen[];
  },

  async obtenerEvento(eventoId: number): Promise<EventoDetalle | null> {
    try {
      const { data } = await api.get(`/eventos/admin/eventos/${eventoId}`);
      return (data?.datos || null) as EventoDetalle | null;
    } catch {
      return null;
    }
  },

  async crearEvento(payload: Partial<EventoDetalle> & { preguntas?: Pregunta[] }) {
    const { data } = await api.post(`/eventos/admin/eventos`, payload);
    return data;
  },

  async actualizarEvento(eventoId: number, payload: Partial<EventoDetalle> & { preguntas?: Pregunta[] }) {
    const { data } = await api.put(`/eventos/admin/eventos/${eventoId}`, payload);
    return data;
  },

  async cambiarEstado(eventoId: number, estado: "PUBLICADO" | "CANCELADO" | "FINALIZADO") {
    const { data } = await api.post(`/eventos/admin/eventos/${eventoId}/estado`, { estado });
    return data;
  },

  /** Dispara el procesamiento de notificaciones pendientes (invitación + recordatorio)
   *  de un evento. Reemplaza al cron cuando no hay scheduler en el servidor. */
  async procesarNotificaciones(eventoId: number) {
    const { data } = await api.post(`/eventos/admin/eventos/${eventoId}/procesar-notificaciones`);
    return data as { info: string; resumen: { invitaciones_enviadas: number; recordatorios_enviados: number; estados_actualizados: number } };
  },

  /** Busca funcionarios para la lista de invitados. Con soloAdministrativos = true
   *  busca sobre el padrón administrativo (el mismo que ve los eventos restringidos). */
  async usuariosActivos(q?: string, soloAdministrativos?: boolean): Promise<UsuarioActivo[]> {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (soloAdministrativos) params.set("solo_administrativos", "1");
    const qs = params.toString() ? `?${params.toString()}` : "";
    const { data } = await api.get(`/eventos/admin/usuarios-activos${qs}`);
    return (data?.datos || []) as UsuarioActivo[];
  },

  async guardarInvitados(eventoId: number, invitados: InvitadoInput[]) {
    const { data } = await api.post(`/eventos/admin/eventos/${eventoId}/invitados`, { invitados });
    return data as { info: string; total: number; omitidos?: number };
  },

  async listarInvitados(eventoId: number) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/invitados`);
    return (data?.datos || []) as any[];
  },

  async guardarTomadores(eventoId: number, tomadores: Array<{ cedula: number | string; nombre?: string }>) {
    const { data } = await api.post(`/eventos/admin/eventos/${eventoId}/tomadores`, { tomadores });
    return data;
  },

  async confirmados(eventoId: number) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/confirmados`);
    return (data?.datos || []) as any[];
  },

  /** Invitados que respondieron que NO asistirán. */
  async rechazados(eventoId: number) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/rechazados`);
    return (data?.datos || []) as any[];
  },

  async presentesAdmin(eventoId: number) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/presentes`);
    return (data?.datos || []) as any[];
  },

  async resultadosEvaluacion(eventoId: number): Promise<Pregunta[]> {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/evaluacion/resultados`);
    return (data?.datos || []) as Pregunta[];
  },

  /** Resumen de pedidos y retiros del evento (base del descuento de nómina). */
  async pedidosAdmin(eventoId: number) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/pedidos`);
    return data as {
      datos: Array<{
        cedula: number;
        /** CodEmpleado del DWH: número de legajo que usa nómina. */
        legajo: number | string | null;
        nombre: string;
        cargo: string;
        estado_confirmacion: string;
        consentimiento_fecha: string | null;
        /** Cuotas elegidas por el invitado (1 = sin cuotas). */
        cuotas: number;
        items: Array<{
          nombre: string;
          cantidad: number;
          /** null si aún no retiró; puede ser menor a cantidad (retiro parcial). */
          cantidad_retirada: number | null;
          precio_unitario: number;
          subtotal: number;
        }>;
        total_pedido: number;
        retirado: boolean;
        fecha_retiro: string | null;
        registrado_por: number | null;
        metodo: string | null;
        total_retirado: number | null;
        tiene_firma: boolean;
      }>;
      items_resumen: Array<{ nombre: string; pedidas: number; retiradas: number }>;
      total_a_descontar: number;
    };
  },

  /** Firma del retiro de un invitado (para verla a demanda). */
  async firmaRetiro(eventoId: number, cedula: number | string) {
    const { data } = await api.get(`/eventos/admin/eventos/${eventoId}/retiros/${cedula}/firma`);
    return data?.datos as { firma_b64: string | null; fecha_retiro: string };
  },

  // -------------------- Invitado --------------------
  async misInvitaciones(): Promise<Invitacion[]> {
    try {
      const { data } = await api.get(`/eventos/mis-invitaciones`);
      return (data?.datos || []) as Invitacion[];
    } catch {
      return [];
    }
  },

  async confirmar(eventoId: number, asistira: boolean, acompanantes = 0) {
    const { data } = await api.post(`/eventos/confirmar`, {
      evento_id: eventoId,
      asistira,
      acompanantes,
    });
    return data;
  },

  /** Invitación con respuesta obligatoria sin responder.
   *  Misma regla de bloqueo (recibos/horas/reclamos) que la actualización de datos.
   *  OJO: propaga el error de red a propósito — el modal reintenta en vez de
   *  asumir "sin pendiente" (en móvil un fetch perdido dejaba pasar el bloqueo). */
  async respuestaPendiente(): Promise<{ bloqueado: boolean; invitacion: any | null }> {
    const { data } = await api.get(`/eventos/respuesta/pendiente`);
    return { bloqueado: Boolean(data?.bloqueado), invitacion: data?.invitacion || null };
  },

  /** Catálogo de ítems del evento + pedido actual del invitado. */
  async obtenerPedido(eventoId: number) {
    const { data } = await api.get(`/eventos/pedido/${eventoId}`);
    return data as {
      items: EventoItem[];
      pedido: PedidoLinea[];
      total: number;
      consentimiento_fecha: string | null;
      /** true = el pedido ya fue confirmado (es único, no se puede modificar). */
      pedido_confirmado: boolean;
      permite_cuotas: boolean;
      max_cuotas: number | null;
      /** Cuotas elegidas al confirmar (null si aún no pidió). */
      cuotas: number | null;
    };
  },

  /** Guarda el pedido (confirma asistencia + consentimiento del descuento).
   *  El pedido es único: el backend rechaza un segundo intento. */
  async guardarPedido(
    eventoId: number,
    items: Array<{ item_id: number; cantidad: number }>,
    aceptaDescuento: boolean,
    acompanantes = 0,
    cuotas = 1
  ) {
    const { data } = await api.post(`/eventos/pedido/${eventoId}`, {
      items,
      acepta_descuento: aceptaDescuento,
      acompanantes,
      cuotas,
    });
    return data as { info: string; total: number; cuotas: number };
  },

  /** QR dinámico del invitado para el retiro (eventos con requiere_qr). */
  async miQR(eventoId: number) {
    const { data } = await api.get(`/eventos/mi-qr/${eventoId}`);
    return data as {
      ya_retirado: boolean;
      qr: string | null;
      ttl_seconds?: number;
      nombre?: string;
      retiro?: RetiroInfo;
    };
  },

  // -------------------- Asistencia (tomadores) --------------------
  async misEventosAsistencia() {
    const { data } = await api.get(`/eventos/asistencia/eventos`);
    return (data?.datos || []) as Array<{
      evento_id: number;
      tema: string;
      lugar: string | null;
      estado: string;
      fecha_inicio_evento: string;
      fecha_fin_evento: string;
    }>;
  },

  async buscarInvitado(eventoId: number, cedula: number | string) {
    const { data } = await api.get(
      `/eventos/asistencia/buscar?evento_id=${eventoId}&cedula=${encodeURIComponent(String(cedula))}`
    );
    return data?.datos || null;
  },

  async registrarAsistencia(
    eventoId: number,
    cedula: number | string,
    acompanantesPresentes = 0,
    opciones?: {
      firma_b64?: string;
      qr?: string;
      /** Cuánto de cada ítem retira efectivamente (retiro parcial). */
      items_retirados?: Array<{ item_id: number; cantidad: number }>;
    }
  ) {
    const { data } = await api.post(`/eventos/asistencia/registrar`, {
      evento_id: eventoId,
      cedula,
      acompanantes_presentes: acompanantesPresentes,
      ...(opciones?.firma_b64 ? { firma_b64: opciones.firma_b64 } : {}),
      ...(opciones?.qr ? { qr: opciones.qr } : {}),
      ...(opciones?.items_retirados ? { items_retirados: opciones.items_retirados } : {}),
    });
    return data;
  },

  /** Resuelve un QR de retiro escaneado y devuelve la ficha del invitado. */
  async escanearQR(eventoId: number, qr: string) {
    const { data } = await api.post(`/eventos/asistencia/escanear`, {
      evento_id: eventoId,
      qr,
    });
    return data?.datos || null;
  },

  async presentes(eventoId: number) {
    const { data } = await api.get(`/eventos/asistencia/presentes?evento_id=${eventoId}`);
    return (data?.datos || []) as any[];
  },

  // -------------------- Evaluación --------------------
  /** Devuelve la evaluación pendiente que bloquea recibo/horas/reclamos. */
  async evaluacionPendiente(): Promise<{ bloqueado: boolean; evento: any | null }> {
    try {
      const { data } = await api.get(`/eventos/evaluacion/pendiente`);
      return { bloqueado: Boolean(data?.bloqueado), evento: data?.evento || null };
    } catch {
      return { bloqueado: false, evento: null };
    }
  },

  async obtenerEvaluacion(eventoId: number) {
    const { data } = await api.get(`/eventos/evaluacion/${eventoId}`);
    return data as { evento: any; completada: boolean; preguntas: Pregunta[] };
  },

  async enviarEvaluacion(
    eventoId: number,
    respuestas: Array<{ pregunta_id: number; opcion_id?: number | null; texto?: string }>
  ) {
    const { data } = await api.post(`/eventos/evaluacion/${eventoId}`, { respuestas });
    return data;
  },
};

export default Eventos;
